// How something picked in the builder's rail is brought onto the screen. One place decides it, so the
// three things a merchant can pick — a node of the tree, a marked field, a whole band — all move the
// page the same way, a selection already in plain sight is never yanked, and a reader who has asked
// for less movement is never thrown down the page at all.

/** How much clear space a box needs above and below it before it counts as comfortably on screen. */
const MARGIN = 24;

/** Reports whether the reader has asked for less movement, which every scroll and fade here honours. */
export const reducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Reports whether the page has to move for a box to be readable, given the frame's own height. A box
 * taller than the frame is judged on its top edge alone: there is no scroll position that shows all of
 * a band twice the height of the window, so one whose head is already on screen is left where it is.
 */
export const needsScroll = (top: number, bottom: number, frame: number): boolean => {
  if (top < MARGIN || top > frame - MARGIN) return true;
  return bottom > frame - MARGIN && bottom - top <= frame - MARGIN * 2;
};

/** How long a smooth scroll is given to have started before the page is simply put there instead. */
const SETTLE = 140;

/** Where the page is scrolled to right now, which is what tells us whether a smooth scroll ever began. */
const scrolledTo = (): number => document.scrollingElement?.scrollTop ?? window.scrollY;

/**
 * Scrolls one box into view, and makes sure it actually happened.
 *
 * A preview lives in a frame, and a frame the browser has throttled runs no animation frames — which
 * is exactly what a smooth scroll is made of, so the request is accepted and then silently does
 * nothing. The merchant sees them pick a node in the tree and the canvas sit still. So the smooth
 * scroll is asked for first, and if the page has not begun to move a moment later it is simply put
 * where it belongs. A reader who has asked for less movement skips straight to that.
 */
function scrollTo(host: Element, block: ScrollLogicalPosition): void {
  if (reducedMotion()) {
    host.scrollIntoView({ behavior: 'auto', block });
    return;
  }
  const before = scrolledTo();
  host.scrollIntoView({ behavior: 'smooth', block });
  window.setTimeout(() => {
    if (scrolledTo() !== before) return;
    host.scrollIntoView({ behavior: 'auto', block });
  }, SETTLE);
}

/** Reports whether a box is clipped sideways — inside a carousel's run, say — and so needs scrolling in. */
const clippedSideways = (box: DOMRect): boolean => box.left < 0 || box.right > window.innerWidth;

// Brings one drawn box onto the screen, and only when it is not already somewhere the merchant can read it.
//
// Sideways as well as down: a slide of a carousel sits in a row that scrolls, and a node picked inside
// the third slide is on the page's vertical scroll and still nowhere to be seen. `scrollIntoView` walks
// every scrolling ancestor, so asking for it inline is all the run needs to be moved.
export function scrollMarkIntoView(host: Element | null | undefined, block: ScrollLogicalPosition = 'center'): void {
  if (!host) return;
  const box = host.getBoundingClientRect();
  if (clippedSideways(box)) {
    host.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    return;
  }
  if (!needsScroll(box.top, box.bottom, window.innerHeight)) return;
  scrollTo(host, block);
}
